export const PUBLIC_API = {
  KEY_PREFIX: 'qk_',
  KEY_BYTES: 32,
  DISPLAY_PREFIX_LENGTH: 10,
  KEY_TTL_MONTHS: 12,
  NAME_MAX_LENGTH: 100,
  MAX_ACTIVE_KEYS_PER_OWNER: 10,
  TOUCH_INTERVAL_MS: 60_000,
  PAGE_URL: '/developers/api',
  BACKOFFICE_PAGE_URL: '/backoffice/public-api',
  CAPABILITY_KEY: 'publicApi',
  BASE_PATH: '/api/public/v1',
  SESSION_TOKEN_PREFIX: 'apikey:',
} as const

export const PUBLIC_API_LIMITS = {
  PER_KEY_PER_MINUTE: 120,
  PER_ORG_PER_MINUTE: 600,
  PAGE_DEFAULT: 50,
  PAGE_MAX: 100,
  IDEMPOTENCY_TTL_HOURS: 24,
  IDEMPOTENCY_KEY_MAX_LENGTH: 255,
  WINDOW_MS: 60_000,
  RATE_LIMIT_RETENTION_MS: 3_600_000,
  PURGE_SAMPLE_RATE: 0.01,
} as const

export const API_KEY_REJECTIONS = {
  MISSING: 'missing' as const,
  MALFORMED: 'malformed' as const,
  UNKNOWN: 'unknown' as const,
  REVOKED: 'revoked' as const,
  EXPIRED: 'expired' as const,
  OWNER_INVALID: 'owner_invalid' as const,
} as const

export type IApiKeyRejection = (typeof API_KEY_REJECTIONS)[keyof typeof API_KEY_REJECTIONS]

export const RATE_LIMIT_SCOPE = {
  KEY: 'key' as const,
  ORG: 'org' as const,
} as const

export type IRateLimitScope = (typeof RATE_LIMIT_SCOPE)[keyof typeof RATE_LIMIT_SCOPE]

export const PUBLIC_API_ENDPOINT_KINDS = {
  LIST: 'list' as const,
  ITEM: 'item' as const,
  WRITE: 'write' as const,
  FILE: 'file' as const,
} as const

export type IPublicEndpointKind = (typeof PUBLIC_API_ENDPOINT_KINDS)[keyof typeof PUBLIC_API_ENDPOINT_KINDS]

export const PUBLIC_API_METHODS = {
  GET: 'GET' as const,
  POST: 'POST' as const,
  PUT: 'PUT' as const,
  PATCH: 'PATCH' as const,
  DELETE: 'DELETE' as const,
} as const

export type IPublicApiMethod = (typeof PUBLIC_API_METHODS)[keyof typeof PUBLIC_API_METHODS]

export const PUBLIC_API_PROBLEMS = {
  UNAUTHORIZED: 'Unauthorized',
  FORBIDDEN: 'Forbidden',
  NOT_FOUND: 'Not Found',
  BAD_REQUEST: 'Bad Request',
  CONFLICT: 'Conflict',
  UNPROCESSABLE: 'Unprocessable Entity',
  TOO_MANY_REQUESTS: 'Too Many Requests',
  INTERNAL: 'Internal Server Error',
} as const

export type IPublicApiProblem = (typeof PUBLIC_API_PROBLEMS)[keyof typeof PUBLIC_API_PROBLEMS]

export const PUBLIC_API_HEADERS = {
  REQUEST_ID: 'X-Request-Id',
  RATE_LIMIT: 'RateLimit-Limit',
  RATE_LIMIT_REMAINING: 'RateLimit-Remaining',
  RATE_LIMIT_RESET: 'RateLimit-Reset',
  RETRY_AFTER: 'Retry-After',
  IDEMPOTENCY_KEY: 'Idempotency-Key',
  AUTHORIZATION: 'Authorization',
  CONTENT_TYPE: 'Content-Type',
  PROBLEM_CONTENT_TYPE: 'application/problem+json',
} as const

export const PUBLIC_API_PROBLEM_TYPE = 'about:blank'

export const IDEMPOTENCY_OUTCOMES = {
  NEW: 'new' as const,
  REPLAY: 'replay' as const,
  IN_FLIGHT: 'in_flight' as const,
  MISMATCH: 'mismatch' as const,
} as const

export type IIdempotencyOutcome = (typeof IDEMPOTENCY_OUTCOMES)[keyof typeof IDEMPOTENCY_OUTCOMES]

export const PUBLIC_API_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL: 500,
} as const

export const PUBLIC_API_DETAILS = {
  INVALID_KEY: 'A valid API key is required in the Authorization header',
  NO_ACCESS: 'Your organization does not have access to the public API',
  ENDPOINT_DISABLED: 'This endpoint is currently disabled',
  MODULE_CLOSED: 'Your organization does not have access to this module',
  RATE_LIMITED: 'Rate limit exceeded, retry after the window resets',
  IDEMPOTENCY_MISMATCH: 'This Idempotency-Key was already used with a different request',
  IDEMPOTENCY_IN_FLIGHT: 'A request with this Idempotency-Key is still being processed',
  INVALID_PAGE: 'Invalid cursor or limit',
  FOREIGN_ORGANIZATION: 'This API key cannot access another organization',
  UNEXPECTED: 'Unexpected error',
  INVALID_IDEMPOTENCY_KEY: 'Invalid Idempotency-Key',
} as const

export const PUBLIC_API_BEARER_PREFIX = /^bearer\s+/i

export const PUBLIC_API_IDEMPOTENT_METHODS: readonly string[] = [
  PUBLIC_API_METHODS.POST,
  PUBLIC_API_METHODS.PUT,
  PUBLIC_API_METHODS.PATCH,
  PUBLIC_API_METHODS.DELETE,
]

export const PUBLIC_API_PAGINATION_PARAMS = {
  CURSOR: 'cursor',
  LIMIT: 'limit',
  DEFAULT_LIST_FIELD: 'data',
  DEFAULT_SORT_KEY: 'id',
} as const

export const PUBLIC_API_ORGANIZATION_FIELD = 'organizationId'

export const PUBLIC_API_HEAD_METHOD = 'HEAD'

export const PUBLIC_API_SUCCESS_STATUS_RANGE = { MIN: 200, MAX: 299 } as const

// Client errors a retry with the same request would get again; anything else (401, 403, 429, ...) may change.
export const PUBLIC_API_STORED_ERROR_STATUSES: readonly number[] = [
  PUBLIC_API_STATUS.BAD_REQUEST,
  PUBLIC_API_STATUS.NOT_FOUND,
  PUBLIC_API_STATUS.CONFLICT,
  PUBLIC_API_STATUS.UNPROCESSABLE,
]

export const API_KEY_STATUS = {
  ACTIVE: 'active' as const,
  REVOKED: 'revoked' as const,
  EXPIRED: 'expired' as const,
} as const

export type IApiKeyStatus = (typeof API_KEY_STATUS)[keyof typeof API_KEY_STATUS]
